import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faPen, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import styles from './DirectoryContent.module.scss';
import { Dropdown } from 'components/UI/V3/Dropdown/Dropdown';
import { DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { useDropdown } from 'components/UI/V3/Dropdown/DropdownContext';
import AddContactModal from '../Add/AddContactModal';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import ConfirmDialog from 'components/UI/ConfirmDialog/ConfirmDialog';
import { RootState } from 'store';
import { Contact, ContactType, getContactTypeDisplayName } from 'model/contact';
import { fetchContactsThunk, deleteContactThunk, saveContactThunk } from 'actions/contact/ContactActions';
import { formatPhoneNumber } from '@spike/phone-utils';
import { SaveContact } from 'model/contact';
import { useOrganizationsContext } from 'components/Organizations/OrganizationsProvider';

const contactTypes = [
    { value: ContactType.FirstEmergency, label: 'First Emergency Contact' },
    { value: ContactType.SecondEmergency, label: 'Second Emergency Contact' },
    { value: ContactType.Financial, label: 'Financial Contact' },
    { value: ContactType.Security, label: 'Security Contact' },
    { value: ContactType.Support, label: 'Support Contact' },
    { value: ContactType.Technical, label: 'Technical Contact' }
];

// Internal component that has access to dropdown context
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ContactTypeDropdownItems: React.FC<{
    onSelectType: (contactType: ContactType) => void;
    existingContactTypes: ContactType[];
}> = ({ onSelectType, existingContactTypes }) => {
    const { setOpen } = useDropdown();

    return (
        <>
            {contactTypes.map(type => {
                const isDisabled = existingContactTypes.includes(type.value);

                return (
                    <DropdownItem
                        key={type.value}
                        disabled={isDisabled}
                        onSelect={() => {
                            if (!isDisabled) {
                                onSelectType(type.value);
                                setOpen(false); // Manually close the dropdown
                            }
                        }}
                    >
                        {type.label}
                    </DropdownItem>
                );
            })}
        </>
    );
};

export const DirectoryContent: React.FC = () => {
    // Redux
    const dispatch = useDispatch();
    const { contacts, loading } = useSelector((state: RootState) => state.contacts);

    // Organizations from context
    const { parentAccounts } = useOrganizationsContext();

    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
    const [selectedContactType, setSelectedContactType] = useState<ContactType | ''>('');
    const [editingContactId, setEditingContactId] = useState<number | undefined>(undefined);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);

    // Fetch contacts when parent organization is available
    useEffect(() => {
        const fetchContacts = async () => {
            const parentOrganization = parentAccounts[0];
            const organizationId = parentOrganization?.id;

            if (organizationId) {
                dispatch(fetchContactsThunk(organizationId));
            }
        };

        if (parentAccounts.length > 0 && contacts.length === 0) {
            fetchContacts();
        }
    }, [dispatch, parentAccounts, contacts.length]);

    const handleAddContact = (contactType: ContactType) => {
        setSelectedContactType(contactType);
        setEditingContactId(undefined);
        setIsAddContactModalOpen(true);
    };

    const handleEditContact = (contact: Contact) => {
        setSelectedContactType(contact.contactType);
        setEditingContactId(contact.id);
        setIsAddContactModalOpen(true);
    };

    const handleDeleteContact = (contact: Contact) => {
        setContactToDelete(contact);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (contactToDelete) {
            const parentOrganization = parentAccounts[0];
            if (parentOrganization) {
                dispatch(deleteContactThunk(contactToDelete.id, parentOrganization.id));
            }
        }
        handleCancelDelete();
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setContactToDelete(null);
    };

    const handleSaveContact = async (
        contactData: {
            firstName: string;
            lastName: string;
            phoneNumber: string;
            email: string;
            contactType: ContactType | string;
        },
        contactId?: number
    ) => {
        const parentOrganization = parentAccounts[0];
        if (!parentOrganization) return;

        const contactTypeValue = contactId ? contactData.contactType : selectedContactType;

        const contactTypeEntry = contactTypes.find(ct => ct.value === contactTypeValue);

        if (!contactTypeEntry) {
            return;
        }

        const saveContactData: SaveContact = {
            organizationId: parentOrganization.id,
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            phone: contactData.phoneNumber,
            email: contactData.email,
            contactType: contactTypeEntry.value
        };

        if (contactId) {
            // Editing existing contact
            await dispatch(saveContactThunk(saveContactData, contactId));
        } else {
            // Creating new contact
            await dispatch(saveContactThunk(saveContactData));
        }

        setIsAddContactModalOpen(false);
        setSelectedContactType('');
        setEditingContactId(undefined);
    };

    const handleCloseContactModal = () => {
        setIsAddContactModalOpen(false);
        setSelectedContactType('');
        setEditingContactId(undefined);
    };

    const renderEmptyState = () => <div className={styles.emptyState}></div>;

    const renderContactsList = () => (
        <div className={styles.contactsList}>
            <div className={styles.contactsGrid}>
                {contacts.map(contact => (
                    <div
                        key={contact.id}
                        className={styles.contactCard}
                    >
                        <div className={styles.contactHeader}>
                            <h3 className={styles.contactType}>{getContactTypeDisplayName(contact.contactType)}</h3>
                            <div className={styles.contactActions}>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => handleDeleteContact(contact)}
                                    title="Delete"
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                                <button
                                    className={styles.actionButton}
                                    onClick={() => handleEditContact(contact)}
                                    title="Edit"
                                >
                                    <FontAwesomeIcon icon={faPen} />
                                </button>
                            </div>
                        </div>

                        <div className={styles.contactInfo}>
                            <p className={styles.contactName}>
                                {contact.firstName} {contact.lastName}
                            </p>
                            <p className={styles.contactPhone}>{formatPhoneNumber(contact.phone, 'us')}</p>
                            <p className={styles.contactEmail}>{contact.email}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className={styles.directoryContent}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <div>
                        <h1 className={styles.title}>Directory</h1>
                    </div>
                    <div>
                        <div className={styles.addContactDropdown}>
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        label="Add contact"
                                        size="large"
                                        variant="outlined"
                                        rightIcon={faAngleDown}
                                    />
                                </DropdownTrigger>

                                <DropdownContent
                                    minWidth={220}
                                    align="end"
                                >
                                    <ContactTypeDropdownItems
                                        onSelectType={handleAddContact}
                                        existingContactTypes={contacts.map(c => c.contactType)}
                                    />
                                </DropdownContent>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                {contacts.length === 0 && !loading && <p className={styles.emptyText}>No contacts added.</p>}
            </div>

            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loadingContainer}>
                        <Spinner size={32} />
                    </div>
                ) : contacts.length === 0 ? (
                    renderEmptyState()
                ) : (
                    renderContactsList()
                )}
            </div>

            {/* Add Contact Modal */}
            {selectedContactType && (
                <AddContactModal
                    isOpen={isAddContactModalOpen}
                    onClose={handleCloseContactModal}
                    contactType={selectedContactType as ContactType}
                    contactId={editingContactId}
                    onSave={handleSaveContact}
                    existingContactTypes={contacts.map(c => c.contactType)}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={isDeleteDialogOpen}
                title="Delete Contact"
                question={
                    contactToDelete
                        ? `Are you sure you want to delete ${contactToDelete.firstName} ${contactToDelete.lastName}?`
                        : 'Are you sure you want to delete this contact?'
                }
                subquestion="This action cannot be undone."
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Delete"
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                processing={loading}
                className={styles.deleteConfirmDialog}
            />
        </div>
    );
};

export default DirectoryContent;
